import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import { ScoreGauge } from "../../components/ScoreGauge";
import { CompetencyRadar } from "../../components/CompetencyRadar";
import { COMPETENCY_LABELS, scoreColor, levelMeta } from "../../lib/scoring";
import {
  Award, CheckCircle2, TrendingUp, GraduationCap, ArrowRight, Star, ShieldCheck,
  Sparkles, Loader2, Target,
} from "lucide-react";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>
);

export default function Result() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [res, setRes] = useState(null);
  const [intel, setIntel] = useState(null);

  useEffect(() => {
    api.get(`/interviews/${id}/result`).then((r) => setRes(r.data)).catch(() => navigate("/candidate"));
    api.get(`/interviews/${id}/intelligence`).then((r) => setIntel(r.data)).catch(() => {});
  }, [id, navigate]);

  if (!res) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const iv = res.interview;
  const meta = levelMeta(iv.overall);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header banner */}
      <Card className="p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative flex flex-col sm:flex-row items-center gap-8">
          <ScoreGauge value={iv.overall} size={190} testid="result-gauge" />
          <div className="flex-1 text-center sm:text-left">
            <div className="inline-flex items-center gap-2 text-xs font-bold text-brand bg-brand-light px-3 py-1.5 rounded-full mb-3">
              <Sparkles className="h-3.5 w-3.5" /> Your Mock2Crack Score
            </div>
            <h1 className="font-heading text-4xl font-extrabold tracking-tight text-ink num">{iv.overall} <span className="text-2xl text-ink-faint">/ 100</span></h1>
            <div className="mt-2 inline-block text-sm font-bold px-3 py-1 rounded-full" style={{ background: meta.bg, color: meta.text }}>{meta.label}</div>
            <p className="text-ink-soft text-sm mt-3">{iv.role_name} · {iv.mode_icon || "🎥"} {iv.mode_label || (iv.type === "ai" ? "AI Video Interview" : "Human Expert Interview")} · Attempt {iv.attempt_number}</p>
          </div>
          <div className="flex flex-col gap-2 w-full sm:w-auto">
            <Link to={`/scorecard/${iv.verification_id}`} target="_blank" data-testid="view-credential"
              className="inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 px-5 py-3 rounded-full transition-colors">
              <ShieldCheck className="h-4 w-4" /> Verified Credential
            </Link>
            <button data-testid="reinterview" onClick={() => navigate("/candidate/book")}
              className="inline-flex items-center justify-center gap-2 text-sm font-bold text-brand bg-brand-light hover:bg-blue-100 px-5 py-3 rounded-full transition-colors">
              <Target className="h-4 w-4" /> Take Another
            </button>
          </div>
        </div>
      </Card>

      {/* Voice communication analysis (AI Voice interviews) */}
      {iv.voice_analysis && (
        <Card className="p-6" data-testid="voice-analysis-card">
          <div className="font-heading font-bold text-ink mb-1">📞 Voice Communication Analysis</div>
          <p className="text-xs text-ink-soft mb-4">How you sounded during your AI voice interview</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ["Communication", iv.voice_analysis.communication],
              ["Clarity", iv.voice_analysis.clarity],
              ["Fluency", iv.voice_analysis.fluency],
              ["Pace", iv.voice_analysis.pace],
              ["Conciseness", iv.voice_analysis.conciseness],
              ["Confidence", iv.voice_analysis.confidence],
              ["Response Structure", iv.voice_analysis.response_structure],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-slate-200 p-3.5">
                <div className="text-xs font-semibold text-ink-soft mb-1">{k}</div>
                <div className="num font-bold text-2xl" style={{ color: scoreColor(v) }}>{v}</div>
              </div>
            ))}
            <div className="rounded-xl border border-slate-200 p-3.5">
              <div className="text-xs font-semibold text-ink-soft mb-1">Filler Words</div>
              <div className="font-bold text-lg text-ink">{iv.voice_analysis.filler_words}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Next Best Action + Before/After (Phase 2) */}
      {intel && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-6 bg-slate-900 text-white border-slate-900">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-300 mb-2"><Sparkles className="h-4 w-4" /> Your Next Best Action</div>
            <div className="font-heading text-xl font-bold">{intel.next_action.title}</div>
            <p className="text-slate-300 text-sm mt-1">{intel.next_action.detail}</p>
            {intel.next_action.expected_gain > 0 && <div className="mt-3 text-xs num font-bold text-emerald-300">Expected +{intel.next_action.expected_gain} readiness pts</div>}
            <button onClick={() => navigate("/candidate/readiness")} data-testid="result-readiness"
              className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full transition-colors">
              Open Readiness Profile <ArrowRight className="h-4 w-4" />
            </button>
          </Card>
          {intel.before_after ? (
            <Card className="p-6" data-testid="before-after">
              <div className="font-heading font-bold text-ink mb-4">Before vs After</div>
              <div className="flex items-center justify-center gap-6 mb-4">
                <div className="text-center"><div className="text-xs text-ink-faint">Previous</div><div className="num font-bold text-3xl text-ink-soft">{intel.before_after.previous}</div></div>
                <ArrowRight className="h-5 w-5 text-ink-faint" />
                <div className="text-center"><div className="text-xs text-ink-faint">Current</div><div className="num font-bold text-3xl text-ink">{intel.before_after.current}</div></div>
                <div className="text-center"><div className="text-xs text-ink-faint">Change</div><div className="num font-bold text-3xl" style={{ color: intel.before_after.improvement >= 0 ? "#10B981" : "#EF4444" }}>{intel.before_after.improvement >= 0 ? "+" : ""}{intel.before_after.improvement}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                {intel.before_after.deltas.map((x) => (
                  <div key={x.skill} className="flex justify-between text-sm"><span className="text-ink-soft">{x.label}</span><span className="num font-bold" style={{ color: x.delta > 0 ? "#10B981" : x.delta < 0 ? "#EF4444" : "#94A3B8" }}>{x.delta > 0 ? "+" : ""}{x.delta}</span></div>
                ))}
              </div>
            </Card>
          ) : (
            <Card className="p-6 grid place-items-center text-center"><div><div className="font-heading font-bold text-ink">Before vs After</div><p className="text-sm text-ink-soft mt-2">This is your first interview — complete another to compare improvement.</p></div></Card>
          )}
        </div>
      )}

      {/* Breakdown + radar */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="font-heading font-bold text-ink mb-4">Score Breakdown</div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {Object.entries(COMPETENCY_LABELS).map(([k, label]) => {
              const v = res.competencies[k];
              return (
                <div key={k} data-testid={`breakdown-${k}`}>
                  <div className="flex justify-between text-sm mb-1.5"><span className="font-medium text-ink-soft">{label}</span><span className="num font-bold text-ink">{v}</span></div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v}%`, background: scoreColor(v) }} /></div>
                </div>
              );
            })}
          </div>
          {iv.coding && (
            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm num">
              <span className="font-bold text-ink">Coding Assessment</span>
              <span className="text-emerald-600 font-semibold">{iv.coding.passed}/{iv.coding.total} passed</span>
              <span className="text-ink-soft">{iv.coding.execution_ms} ms</span>
              <span className="text-brand font-bold ml-auto">Score {iv.coding.score}</span>
            </div>
          )}
        </Card>
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-2">Skill Radar</div>
          <CompetencyRadar competencies={res.competencies} height={260} />
        </Card>
      </div>

      {/* Strengths + skill gaps */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Your Strengths</div>
          <div className="space-y-2.5">
            {res.strengths.map((s, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-ink-soft"><Star className="h-4 w-4 text-amber-400 fill-amber-300" /> {s}</div>
            ))}
          </div>
        </Card>
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Target className="h-4 w-4 text-red-500" /> Skill Gaps</div>
          <div className="space-y-4">
            {res.skill_gaps.length ? res.skill_gaps.slice(0, 5).map((g) => (
              <div key={g.skill}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-semibold text-ink">{g.label}</span>
                  <span className="num text-xs"><span className="text-ink-faint">{g.current} → {g.target}</span> <span className="text-red-500 font-bold">-{g.gap}</span></span>
                </div>
                <div className="relative h-2.5 rounded-full bg-slate-100">
                  <div className="absolute h-full rounded-full bg-brand" style={{ width: `${g.current}%` }} />
                  <div className="absolute top-1/2 -translate-y-1/2 h-4 w-0.5 bg-emerald-500" style={{ left: `${g.target}%` }} />
                </div>
                <p className="text-xs text-ink-faint mt-1">{g.recommendation}</p>
              </div>
            )) : <div className="text-sm text-ink-soft">No significant gaps — you're interview ready!</div>}
          </div>
        </Card>
      </div>

      {/* Improvement plan */}
      <Card className="p-6">
        <div className="font-heading font-bold text-ink mb-4">Your Improvement Plan</div>
        <div className="grid md:grid-cols-3 gap-4">
          {res.improvement_plan.map((p) => (
            <div key={p.priority} className="rounded-xl border border-slate-200 p-5" data-testid={`plan-${p.priority}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-white bg-brand px-2.5 py-1 rounded-full">Priority {p.priority}</span>
                <span className="text-xs num font-bold text-emerald-600">{p.estimated_improvement} pts</span>
              </div>
              <div className="font-heading font-bold text-ink">{p.label}</div>
              <div className="text-xs text-ink-faint num mb-3">Current {p.current} · Target {p.target}</div>
              <ul className="space-y-1.5">
                {p.actions.map((a, i) => <li key={i} className="flex items-start gap-2 text-xs text-ink-soft"><span className="num text-brand font-bold">{i + 1}.</span> {a}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Card>

      {/* Courses */}
      <Card className="p-6">
        <div className="font-heading font-bold text-ink mb-1 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-brand" /> Recommended For You</div>
        <p className="text-xs text-ink-soft mb-4">Curated courses based on your skill gaps.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {res.recommended_courses.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 p-5 hover:border-brand hover:shadow-card transition-all" data-testid={`course-${c.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-ink-soft">{c.provider}</span>
                <span className="text-xs font-bold text-amber-500 flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400" /> {c.rating}</span>
              </div>
              <div className="font-semibold text-ink text-sm leading-tight">{c.title}</div>
              <div className="text-xs text-ink-faint mt-1">{c.duration} · For {c.skill_label}</div>
              <p className="text-xs text-ink-soft mt-2">{c.why}</p>
              <button className="mt-3 w-full text-xs font-bold text-brand hover:bg-brand-light px-4 py-2 rounded-full transition-colors">Start Learning</button>
            </div>
          ))}
        </div>
      </Card>

      {/* Re-interview loop */}
      <Card className="p-8 text-center bg-slate-900 text-white border-slate-900">
        <TrendingUp className="h-8 w-8 mx-auto text-brand mb-3" />
        <h2 className="font-heading text-2xl font-bold">Ready to measure your improvement?</h2>
        <p className="text-slate-300 text-sm mt-1">Take another interview and watch your Einstein Score climb.</p>
        <button data-testid="reinterview-bottom" onClick={() => navigate("/candidate/book")}
          className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-6 py-3 rounded-full shadow-ambient transition-colors">
          Take Another Interview <ArrowRight className="h-4 w-4" />
        </button>
      </Card>
    </div>
  );
}
